/**
 * TrialExpiredModal & TrialWarningBanner
 *
 * Shown automatically on protected routes when a user's free trial has ended
 * (or is about to end). Uses the same subscription API the rest of the app uses.
 *
 * - Products WITHOUT a free tier (autopilot, intelligence-navigator) -> modal is
 *   non-dismissible; user must choose a paid plan.
 * - Products WITH a free tier (knowledge-iq, data-iq) -> user can dismiss and
 *   continue on the free plan.
 * - When the trial is within 3 days of expiry a non-blocking warning banner
 *   appears at the top of the page.
 * - Team members see a "service interrupted" screen (cannot upgrade themselves).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle, Clock, ArrowRight, X, Check, Users, HardDrive, MessageSquare, Eye,
} from 'lucide-react';
import subscriptionService from '../../services/subscriptionService';
import { useAuth } from '../../contexts/AuthContext';
import {
  buildTrialFallbackPlans,
  describeVerificationFailure,
  formatVerificationError,
  resolvePlanName,
  resolvePlanTagline,
  resolveProductLabel,
  resolveTrialPlanFeatures,
} from '../../utils/planCatalogI18n';

const HAS_FREE_TIER = {
  autopilot: false,
  'knowledge-iq': true,
  'data-iq': true,
  'intelligence-navigator': true,
};

const getTrialFeatureIcon = (text) => {
  const lower = String(text || '').toLowerCase();
  if (lower.includes('seat')) return Users;
  if (lower.includes('credit') || lower.includes('storage') || lower.includes('upload') || lower.includes('row') || lower.includes('kb') || lower.includes('mb') || lower.includes('gb')) return HardDrive;
  if (lower.includes('chatbot') || lower.includes('automation') || lower.includes('module') || lower.includes('agent')) return MessageSquare;
  if (lower.includes('vision')) return Eye;
  return Check;
};

const WARNING_DAYS = 3;
const PENDING_BILLING_INTENT_KEY = 'xpherium:pending-billing-intent';
const STANDALONE_SUB_VIEW_KEY = 'xpherium:standalone-subscription-view';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.xpherium.com/api';

// Warning banner for trials ending soon
export const TrialWarningBanner = ({ daysRemaining, product, onUpgrade, onDismiss }) => {
  const { t } = useTranslation('billing');
  const label = resolveProductLabel(t, product);
  return (
    <motion.div
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -60, opacity: 0 }}
      className="fixed top-0 left-0 right-0 z-[99] bg-gradient-to-r from-blue-600/95 to-indigo-600/95 backdrop-blur-sm shadow-lg"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-white flex-shrink-0" />
          <p className="text-white text-sm font-medium">
            {t('trial.bannerEndsIn', { product: label, count: daysRemaining })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={onUpgrade} className="px-4 py-1.5 bg-white text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-50 transition-colors">
            {t('trial.upgradeNow')}
          </button>
          <button onClick={onDismiss} className="p-1.5 text-white/70 hover:text-white transition-colors" aria-label={t('trial.dismiss')}>
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const TrialExpiredModal = ({ product }) => {
  const { t } = useTranslation('billing');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [dynamicPlans, setDynamicPlans] = useState([]);
  // Default to YEARLY so the headline per-month price is the lower
  // annually-billed number (matches the marketing pricing page and the
  // upgrade popup). The user can still toggle to monthly if they prefer.
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [dismissed, setDismissed] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [allowStandaloneSubscriptionView, setAllowStandaloneSubscriptionView] = useState(false);
  const [verificationError, setVerificationError] = useState('');

  const hasFree = HAS_FREE_TIER[product] ?? false;
  const productLabel = resolveProductLabel(t, product);
  const fallbackPlans = buildTrialFallbackPlans(t, product);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token) {
      setLoading(false);
      return;
    }

    const describeError = (err) => describeVerificationFailure(t, err);

    const buildStatusFromCurrentSubscription = (currentResponse) => {
      const subscription = currentResponse?.subscription;
      if (!subscription) {
        return null;
      }

      const now = Date.now();
      const trialEndAt = subscription?.trialEndDate ? new Date(subscription.trialEndDate).getTime() : null;
      const isTrialExpired = subscription?.status === 'expired'
        || (subscription?.status === 'trialing' && Number.isFinite(trialEndAt) && trialEndAt < now);
      const isActive = subscription?.status === 'past_due'
        || ((subscription?.status === 'active' || subscription?.status === 'trialing') && !isTrialExpired);
      const daysRemaining = subscription?.status === 'trialing' && Number.isFinite(trialEndAt)
        ? Math.max(0, Math.ceil((trialEndAt - now) / (1000 * 60 * 60 * 24)))
        : 0;

      return {
        hasSubscription: true,
        isActive,
        isTrialExpired,
        daysRemaining,
        subscription,
        isTeamMember: false,
      };
    };

    try {
      setVerificationError('');
      const [statusRes, plansRes] = await Promise.allSettled([
        subscriptionService.getSubscriptionStatus(product),
        subscriptionService.getPricingPlans(product),
      ]);
      if (statusRes.status === 'fulfilled') {
        setSubscriptionData(statusRes.value);
      } else {
        // Backend-authoritative fallback: if status endpoint is transiently unavailable,
        // derive entitlement from current subscription record.
        try {
          const currentResponse = await subscriptionService.getCurrentSubscription(product);
          const fallbackStatus = buildStatusFromCurrentSubscription(currentResponse);
          if (fallbackStatus) {
            setSubscriptionData(fallbackStatus);
          } else {
            setSubscriptionData(null);
            setVerificationError(formatVerificationError(t, { statusCode: 0, reasons: [describeError(statusRes.reason)] }));
          }
        } catch (currentErr) {
          // Final fallback: direct no-timeout status call to avoid wrapper-level timeout failures.
          try {
            const directRes = await fetch(`${API_BASE_URL}/subscriptions/status/${product}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (directRes.ok) {
              const directData = await directRes.json();
              setSubscriptionData(directData);
              setVerificationError('');
            } else {
              setSubscriptionData(null);
              setVerificationError(formatVerificationError(t, { statusCode: directRes.status }));
            }
          } catch (directErr) {
            setSubscriptionData(null);
            setVerificationError(formatVerificationError(t, {
              reasons: [
                describeError(statusRes.reason),
                describeError(currentErr),
                describeError(directErr),
              ],
            }));
          }
        }
      }
      if (plansRes.status === 'fulfilled' && plansRes.value?.success && plansRes.value?.plans) {
        // Highlight Team for IN (the new "missing middle" tier), keep
        // Professional highlighted for the rest of the catalog.
        // v2: Professional is the recommended highlight for IN (Team
        // tier was retired in the May 2026 restructure).
        const recommendedFor = 'professional';
        setDynamicPlans(
          plansRes.value.plans
            .filter((p) => p.id !== 'enterprise')
            .map((p) => ({
              id: p.id, name: p.name,
              monthly: p.monthlyPrice ?? 0, yearly: p.yearlyPrice ?? 0,
              tagline: p.tagline || '', recommended: p.id === recommendedFor,
              isFree: p.id === 'free', features: p.features || [],
            })),
        );
      }
    } catch (err) {
      console.error('TrialExpiredModal: error fetching data', err);
    } finally {
      setLoading(false);
    }
  }, [product, t]);

  // Fetch on mount and whenever auth user changes (handles login race condition)
  useEffect(() => { fetchData(); }, [fetchData, user]);

  // Keep verification state stable; avoid auto-retry loops that cause visible flicker.
  // Users can explicitly retry from the blocker UI.

  useEffect(() => {
    if (sessionStorage.getItem(`trial_banner_dismissed_${product}`)) setBannerDismissed(true);
    // Only honor dismissal for products with a free tier; non-free products
    // must always block until payment is confirmed
    if (hasFree && sessionStorage.getItem(`trial_modal_dismissed_${product}`)) setDismissed(true);
  }, [product, hasFree]);

  useEffect(() => {
    const cached = sessionStorage.getItem(STANDALONE_SUB_VIEW_KEY);
    if (!cached) return;
    try {
      const parsed = JSON.parse(cached);
      if (parsed?.product === product && parsed?.isSubscriptionView === true) {
        setAllowStandaloneSubscriptionView(true);
      }
    } catch (e) {
      // ignore
    }
  }, [product]);

  useEffect(() => {
    const handleSubscriptionViewChange = (e) => {
      if (!e?.detail?.product || e.detail.product !== product) return;
      const isSubscriptionView = Boolean(e.detail.isSubscriptionView);
      setAllowStandaloneSubscriptionView(isSubscriptionView);
      if (isSubscriptionView) {
        sessionStorage.setItem(STANDALONE_SUB_VIEW_KEY, JSON.stringify({
          product,
          isSubscriptionView: true,
          updatedAt: Date.now(),
        }));
      } else {
        sessionStorage.removeItem(STANDALONE_SUB_VIEW_KEY);
      }
    };
    window.addEventListener('xpherium:subscription-view-changed', handleSubscriptionViewChange);
    return () => window.removeEventListener('xpherium:subscription-view-changed', handleSubscriptionViewChange);
  }, [product]);

  // Don't render the blocking modal on the subscription/billing page -- let the user pay.
  // For standalone products, rely on explicit subscription-view state (not URL query),
  // so users cannot stay unblocked after leaving billing.
  const isStandaloneSubscriptionView =
    (product === 'autopilot' || product === 'data-iq' || product === 'knowledge-iq') &&
    allowStandaloneSubscriptionView;
  const isOnSubscriptionPage =
    location.pathname.includes('/subscription') ||
    location.pathname.includes('/billing') ||
    isStandaloneSubscriptionView;
  const hasToken = Boolean(localStorage.getItem('token') || localStorage.getItem('auth_token'));

  const renderSubscriptionVerificationBlocker = (isRetryState = false) => (
    <div className="fixed inset-0 z-[100] bg-[#0B1120] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6 text-center">
        {isRetryState && (
          <h2 className="text-xl font-semibold text-white mb-3">
            {t('trial.verifyFailedTitle')}
          </h2>
        )}
        {isRetryState && (
          <p className="text-slate-400 text-sm mb-6">
            {t('trial.verifyFailedBody')}
          </p>
        )}
        {isRetryState && verificationError && (
          <p className="text-xs text-slate-500 mb-4">{verificationError}</p>
        )}
        {isRetryState ? (
          <button
            onClick={() => {
              setLoading(true);
              fetchData();
            }}
            className="w-full py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
          >
            {t('trial.retry')}
          </button>
        ) : (
          <div className="w-8 h-8 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto" />
        )}
      </div>
    </div>
  );

  // For products without a free tier, keep a stable blocker while loading
  // to prevent render flicker behind the modal.
  if (loading) {
    if (!hasFree && !isOnSubscriptionPage && hasToken) {
      return renderSubscriptionVerificationBlocker(false);
    }
    return null;
  }

  if (!subscriptionData) {
    if (!hasToken) {
      return null;
    }
    if (!hasFree && !isOnSubscriptionPage) {
      return renderSubscriptionVerificationBlocker(true);
    }
    return null;
  }

  const { isTrialExpired, daysRemaining, hasSubscription, subscription, isTeamMember, isActive } = subscriptionData;

  if (!hasSubscription) return null;
  if (isActive === true) return null;
  if (subscription?.status === 'active' && isTrialExpired !== true) return null;
  if (subscription?.status === 'canceled') return null;

  const isTrialing = subscription?.status === 'trialing';
  const isExpired = subscription?.status === 'expired';
  if (!isTrialing && !isExpired) return null;

  const serviceInterrupted = isExpired || isTrialExpired;

  if (isExpired) {
    // Always show blocking modal
  } else if (!isTrialExpired && daysRemaining > WARNING_DAYS) {
    return null;
  }

  const navigateToSubscription = (planId = null, planNameOverride = null) => {
    if (hasFree) {
      sessionStorage.setItem(`trial_modal_dismissed_${product}`, 'true');
      setDismissed(true);
    }
    const availablePlans = dynamicPlans.length > 0 ? dynamicPlans : fallbackPlans;
    const matchedPlan = planId ? availablePlans.find((p) => p.id === planId) : null;
    const planName = planNameOverride || (matchedPlan ? resolvePlanName(t, product, matchedPlan.id, matchedPlan.name) : planId || null);

    if (planId) {
      // Persist intent in case billing event fires before subscription screen mounts.
      try {
        sessionStorage.setItem(PENDING_BILLING_INTENT_KEY, JSON.stringify({
          product,
          planId,
          planName,
          billingCycle,
          createdAt: Date.now(),
        }));
      } catch (e) {
        // no-op
      }
    }

    if (product === 'intelligence-navigator') {
      navigate('/subscription?trial_expired=true');
      if (planId) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('open-billing-modal', {
            detail: { product, planId, planName, billingCycle },
          }));
        }, 300);
      }
    } else {
      setAllowStandaloneSubscriptionView(true);
      sessionStorage.setItem(STANDALONE_SUB_VIEW_KEY, JSON.stringify({
        product,
        isSubscriptionView: true,
        updatedAt: Date.now(),
      }));
      window.dispatchEvent(new CustomEvent('navigate-to-subscription', { detail: { product, trialExpired: true } }));
      if (planId) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('open-billing-modal', {
            detail: { product, planId, planName, billingCycle },
          }));
        }, 300);
      }
    }
  };

  const handleContinueFree = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      await fetch(`${process.env.REACT_APP_API_URL || 'https://api.xpherium.com/api'}/subscriptions/upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan: 'free', billingCycle: 'monthly', confirmed: true, product }),
      });
    } catch (err) { console.error('Failed to switch to free plan:', err); }
    sessionStorage.setItem(`trial_modal_dismissed_${product}`, 'true');
    setDismissed(true);
    window.location.reload();
  };

  const handleDismissBanner = () => {
    sessionStorage.setItem(`trial_banner_dismissed_${product}`, 'true');
    setBannerDismissed(true);
  };

  // Warning banner (trial ending soon)
  if (isTrialing && !isTrialExpired && daysRemaining <= WARNING_DAYS) {
    if (bannerDismissed) return null;
    return (
      <AnimatePresence>
        <TrialWarningBanner daysRemaining={daysRemaining} product={product} onUpgrade={navigateToSubscription} onDismiss={handleDismissBanner} />
      </AnimatePresence>
    );
  }

  // Team member blocking screen
  if (isTeamMember && serviceInterrupted) {
    return (
      <AnimatePresence>
        <motion.div key="team-blocked" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-md bg-slate-900 rounded-2xl shadow-2xl border border-slate-700/60 overflow-hidden">
            <div className="border-b border-slate-700/60 px-8 py-8">
              <div className="flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-blue-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{t('trial.teamBlockedTitle')}</h2>
                  <p className="text-slate-400 mt-3 text-sm leading-relaxed">
                    {t('trial.teamBlockedBody', { product: productLabel })}
                  </p>
                </div>
              </div>
            </div>
            <div className="px-8 py-5 bg-slate-900/50">
              <p className="text-slate-500 text-xs text-center">{t('trial.dataSafe')}</p>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // When user is on the subscription page, don't block them -- they're there to pay
  if (isOnSubscriptionPage && serviceInterrupted && !hasFree) return null;

  // For products without a free tier, never allow dismissal when service is interrupted
  if (dismissed && (!serviceInterrupted || hasFree)) return null;

  const plans = dynamicPlans.length > 0 ? dynamicPlans : fallbackPlans;

  return (
    <AnimatePresence>
      <motion.div key="trial-expired-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-3xl bg-slate-900 rounded-2xl shadow-2xl border border-slate-700/60 overflow-hidden">

          {/* Header */}
          <div className="border-b border-slate-700/60 px-8 py-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white">{t('trial.expiredTitle', { product: productLabel })}</h2>
              <p className="text-slate-400 mt-2 text-sm">{t('trial.expiredSubtitle', { product: productLabel })}</p>
            </div>
          </div>

          {/* Body */}
          <div className="px-8 py-6">
            {/* Billing Toggle */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex bg-slate-800/80 border border-slate-700/50 rounded-full p-1">
                <button onClick={() => setBillingCycle('monthly')}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'monthly' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}>
                  {t('trial.monthly')}
                </button>
                <button onClick={() => setBillingCycle('yearly')}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'yearly' ? 'bg-white text-slate-900' : 'text-slate-400 hover:text-white'}`}>
                  {t('trial.yearly')} <span className="ml-1.5 text-xs text-emerald-400">{t('trial.save20')}</span>
                </button>
              </div>
            </div>

            {/* Plan cards */}
            <div
              className={`grid gap-4 ${
                plans.length === 2
                  ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto'
                  : plans.length === 4
                    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
                    : 'grid-cols-1 sm:grid-cols-3'
              }`}
            >
              {plans.map((plan) => {
                const price = plan.isFree ? 0 : billingCycle === 'yearly' ? plan.yearly : plan.monthly;
                const features = resolveTrialPlanFeatures(t, product, plan.id, getTrialFeatureIcon);
                const displayName = resolvePlanName(t, product, plan.id, plan.name);
                const displayTagline = resolvePlanTagline(t, product, plan.id, plan.tagline, { trial: true });

                return (
                  <div key={plan.id}
                    className={`relative rounded-xl border p-5 transition-all ${plan.recommended
                      ? 'bg-slate-800/80 border-blue-500/50 shadow-lg shadow-blue-500/10'
                      : 'bg-slate-800/40 border-slate-700/50 hover:border-slate-600'}`}>

                    {plan.recommended && (
                      <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                        <span className="px-3 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
                          {t('trial.recommended')}
                        </span>
                      </div>
                    )}

                    <h3 className="text-lg font-semibold text-white mb-2">{displayName}</h3>

                    <div className="flex items-baseline gap-1 mb-1">
                      {plan.isFree ? (
                        <span className="text-2xl font-bold text-white">{t('trial.free')}</span>
                      ) : (
                        <>
                          <span className="text-slate-400">$</span>
                          <span className="text-2xl font-bold text-white">
                            {(() => {
                              const shown = billingCycle === 'yearly' ? price / 12 : price;
                              const cents = Math.round(Number(shown) * 100) / 100;
                              return Number.isInteger(cents) ? String(cents) : cents.toFixed(2);
                            })()}
                          </span>
                          <span className="text-slate-400 text-xs">{t('trial.perMo')}</span>
                        </>
                      )}
                    </div>

                    {!plan.isFree && billingCycle === 'yearly' && (
                      <p className="text-slate-500 text-xs mb-2">{t('trial.billedAnnually', { amount: price.toLocaleString() })}</p>
                    )}

                    <p className="text-slate-400 text-xs mb-4">{displayTagline}</p>

                    {plan.isFree ? (
                      <button onClick={handleContinueFree}
                        className="w-full py-2.5 rounded-lg text-sm font-medium transition-colors bg-slate-700 hover:bg-slate-600 text-white">
                        {t('trial.continueFree')}
                      </button>
                    ) : (
                      <button onClick={() => navigateToSubscription(plan.id, displayName)}
                        className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${plan.recommended
                          ? 'bg-blue-500 hover:bg-blue-600 text-white'
                          : 'bg-white hover:bg-slate-100 text-slate-900'}`}>
                        {t('trial.choosePlan', { plan: displayName })} <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    )}

                    {features.length > 0 && (
                      <ul className="mt-4 space-y-2 border-t border-slate-700/40 pt-4">
                        {features.map((feat, i) => {
                          const FIcon = feat.icon || Check;
                          return (
                            <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
                              <FIcon className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                              <span>{feat.text}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Enterprise CTA */}
            <div className="mt-5 flex items-center justify-center gap-2 text-sm text-slate-500">
              <span>
                {t('trial.needMore')}{' '}
                <a href="https://www.xpherium.com/contact-sales" target="_blank" rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300">{t('trial.contactSales')}</a>{' '}
                {t('trial.forEnterprise')}
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-4 border-t border-slate-700/60 bg-slate-900/50 flex items-center justify-between">
            <p className="text-slate-500 text-xs">{t('trial.footerChoose', { product: productLabel })}</p>
            {hasFree && (
              <button onClick={handleContinueFree} className="text-slate-400 hover:text-white text-sm font-medium transition-colors">
                {t('trial.continueWithFree')}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TrialExpiredModal;
